import { useState, useEffect, useRef } from "react";
import { Share2, MessageCircle, Facebook, Link as LinkIcon, Check, X, Download } from "lucide-react";
import { entitySlug } from "../utils/slugify";

/**
 * Share button for profile pages.
 * - kind:    "player" | "staff"
 * - id:      profile UUID (or slug--uuid — backend handles both)
 * - title:   optional override for the native share dialog title
 *           (also used to compute the SEO slug)
 *
 * It shares a URL that points at the backend OG endpoint
 * (e.g. https://lefteriafc.cy/api/og/player/nikos-papadopoulos--{uuid}) so
 * messengers scrape rich OG cards. Backwards-compatible — older Greek-encoded
 * URLs still resolve because the slug part is cosmetic.
 */
export default function ShareButton({ kind, id, title }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const ref = useRef(null);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Build a `{slug}--{uuid}` segment so the share URL reads as ASCII
  // (e.g. `nikos-papadopoulos--abc-123`) instead of `%CE%9D...`.
  const seoId = id ? entitySlug(title || "", id) : "";
  let shareUrl;
  if (!id) {
    shareUrl = origin;
  } else if (kind === "player-announce") {
    shareUrl = `${origin}/api/og/player/${seoId}/announce`;
  } else {
    shareUrl = `${origin}/api/og/${kind}/${seoId}`;
  }
  // PNG download URL — the 1200×630 share card PNG so the user can drop it
  // straight into Instagram / WhatsApp Stories without going via OG scrape.
  let downloadUrl = "";
  if (id) {
    if (kind === "player-announce") {
      downloadUrl = `${origin}/api/og/player/${seoId}/announce.png`;
    } else if (kind === "player" || kind === "staff") {
      downloadUrl = `${origin}/api/og/${kind}/${seoId}/card.png`;
    }
  }
  const shareTitle = title || "ΛΕΥΤΕΡΙΑ FC";

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }
  }, [open]);

  const handleNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return true;
      } catch {
        // user cancelled — fall through to menu
      }
    }
    return false;
  };

  const handleTrigger = async () => {
    const usedNative = await handleNative();
    if (!usedNative) setOpen((v) => !v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback for old browsers
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
  };

  const downloadCard = async () => {
    if (!downloadUrl || downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(downloadUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (shareTitle || "lefteria-card").replace(/[^a-z0-9\u0370-\u03ff-]+/gi, "-").toLowerCase();
      a.href = url;
      a.download = `${safeName}-${id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // Fallback — open in a new tab so the user can long-press / right-click save.
      window.open(downloadUrl, "_blank", "noopener");
    } finally {
      setDownloading(false);
    }
  };

  const enc = encodeURIComponent(shareUrl);
  const text = encodeURIComponent(shareTitle);
  const whatsapp = `https://wa.me/?text=${text}%20${enc}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${enc}`;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={handleTrigger}
        className="btn-secondary inline-flex items-center gap-1.5 text-[11px] leading-none whitespace-nowrap"
        data-testid="share-button"
        aria-label="Κοινοποίηση"
      >
        <Share2 size={12} className="shrink-0" />
        <span>Κοινοποίηση</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-60 bg-[#161616] border border-[#2a2a2a] rounded-lg shadow-2xl z-50 overflow-hidden"
          data-testid="share-menu"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#2a2a2a]">
            <span className="text-xs text-zinc-400 uppercase tracking-wider">Κοινοποίηση</span>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" aria-label="Κλείσιμο">
              <X size={14} />
            </button>
          </div>
          <a
            href={whatsapp}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-200 hover:bg-[#1f1f1f] hover:text-white transition-colors"
            data-testid="share-whatsapp"
          >
            <MessageCircle size={16} className="text-emerald-400" /> WhatsApp
          </a>
          <a
            href={facebook}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-200 hover:bg-[#1f1f1f] hover:text-white transition-colors"
            data-testid="share-facebook"
          >
            <Facebook size={16} className="text-[#1877F2]" /> Facebook
          </a>
          <button
            onClick={copy}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-200 hover:bg-[#1f1f1f] hover:text-white transition-colors"
            data-testid="share-copy"
          >
            {copied ? <Check size={16} className="text-emerald-400" /> : <LinkIcon size={16} className="text-[#F5A623]" />}
            <span>{copied ? "Αντιγράφηκε!" : "Αντιγραφή Συνδέσμου"}</span>
          </button>
          {downloadUrl && (
            <button
              onClick={downloadCard}
              disabled={downloading}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-200 hover:bg-[#1f1f1f] hover:text-white transition-colors border-t border-[#2a2a2a] disabled:opacity-60"
              data-testid="share-download-card"
              title="Κατέβασε την εικόνα 1200×630 PNG για Stories"
            >
              <Download size={16} className="text-[#F5A623]" />
              <span>{downloading ? "Λήψη..." : "Λήψη Κάρτας (PNG)"}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
